/**
 * The phone's List — Aaron, Oct 25, phone note 3.3: "the list view should
 * mirror the infinitely scrolling agenda view" of Outlook's phone calendar
 * (`docs/activity.md` §10.2).
 *
 * One list, every day in it: the past above, the future below, loaded a
 * month at a time as it comes near, a sticky heading over each day
 * (*Today · Thursday, October 22*, *Tomorrow · …*, then the weekday and the
 * date) with the forecast at its right (§6.9), and *No events* under a day
 * with nothing on it. **Every empty day is drawn**, as Outlook draws it: the
 * week strip above follows the day at the top of the list and a date tapped
 * there scrolls the list to it, so every date needs a heading of its own to
 * be at the top — a run of empty days folded into one row would leave the
 * strip nowhere to point — and a gap in the list stays as long as the gap in
 * the week.
 *
 * A day reads as Board 13 A's list did: *4 stops · about 52 min driving*,
 * *Leave the office by 8:11 · 19 min* (in the past band once it has gone),
 * a card per stop with its 44 px time column, the drive between stops, the
 * now line, timed tasks as amber rows and blocks hatched. With several people
 * it is one list in time order, each card in its person's colour with their
 * face (§10.2), and no drives. A stop opens its stop page (§10.3), a task its
 * Task sheet (§8.2); a task's circle ticks it.
 *
 * It opens on the selected day and calls `onPickDay` with the day at the top
 * once scrolling settles, so the strip can follow; a day picked elsewhere
 * scrolls the list to it.
 */
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  KIND_STYLES, addDays, agendaHeading, aboutDrivingWords, entriesOn, minutesLeftWords, monthKeyOf, monthKeysBetween,
  toneOf,
  type ActivityDay, type CalendarAnswer, type CalendarDrive, type CalendarItem, type CalendarPersonDays, type DayEntry,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Face } from '../faces.js'
import { Glyph } from '../glyphs.js'
import { Forecast } from './blocks.js'
import { HATCH, NOW_RED, PAST_TINT, TASK_BOX, TASK_LINE, TASK_TEXT, clock } from './look.js'
import { useNowMinutes } from './now.js'
import { openCalendarItem } from './open-item.js'
import { useCalendarMonths } from './use-months.js'
import { useEndless } from './use-endless.js'

export interface PhoneAgendaProps {
  /** The selected day, `YYYY-MM-DD` (`state.calendar.date`). */
  date: string
  /** The people shown, in the people button's order. */
  people: string[]
  /** The day at the top of the list, once scrolling settles — the week strip follows it. */
  onPickDay: (date: string) => void
}

/** Days drawn either side of the day opened on, then four weeks at a time, at most twenty-eight weeks. */
const AROUND = { before: 14, after: 42 }
const STEP = 28
const MOST = 196
/** How long the top day must stay the top day before the strip is told. */
const SETTLE_MS = 200

const DRIVE_TONE: Record<CalendarDrive['tone'], string> = {
  ok: 'text-text-muted', tight: 'text-warn', short: 'text-danger',
}

const isStop = (item: CalendarItem) => item.kind === 'appointment' || item.kind === 'meeting'

const daysFrom = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000) + 1

export function PhoneAgenda({ date, people, onPickDay }: PhoneAgendaProps) {
  const { state, today } = useActivity()
  const show = state.calendar.show
  const [range, setRange] = useState({ from: addDays(date, -AROUND.before), to: addDays(date, AROUND.after) })
  const [top, setTop] = useState(date)
  const days = useMemo(() => {
    const out: string[] = []
    for (let day = range.from; day <= range.to; day = addDays(day, 1)) out.push(day)
    return out
  }, [range])
  /*
   * The month the screen is in, the one before it and the next, asked for as
   * they come near — the one before whole, so scrolling up meets days already
   * their height rather than growing above the thumb.
   */
  const near = useMemo(() => monthKeysBetween(addDays(top, -35), addDays(top, 40)), [top])
  const drawn = useMemo(() => monthKeysBetween(range.from, range.to), [range])
  const { answers, errors, reload } = useCalendarMonths(people, near, drawn)
  const nowAnswer = answers.get(monthKeyOf(today)) ?? null
  const now = useNowMinutes(nowAnswer)

  /* The day this list last told the shell about — it coming back as `date` is no reason to move. */
  const reported = useRef(date)
  const settle = useRef<number | undefined>(undefined)
  const latestDate = useRef(date)
  latestDate.current = date
  const pick = useRef(onPickDay)
  pick.current = onPickDay
  const onTopChange = useCallback((key: string) => {
    setTop(key)
    window.clearTimeout(settle.current)
    settle.current = window.setTimeout(() => {
      if (key === latestDate.current) return
      reported.current = key
      pick.current(key)
    }, SETTLE_MS)
  }, [])
  useEffect(() => () => window.clearTimeout(settle.current), [])

  const scroller = useRef<HTMLDivElement>(null)
  const { scrollToKey } = useEndless(scroller, {
    attr: 'data-day',
    onTopChange,
    onNearTop: () => setRange((was) => {
      const from = addDays(was.from, -STEP)
      return { from, to: daysFrom(from, was.to) > MOST ? addDays(from, MOST - 1) : was.to }
    }),
    onNearBottom: () => setRange((was) => {
      const to = addDays(was.to, STEP)
      return { from: daysFrom(was.from, to) > MOST ? addDays(to, -(MOST - 1)) : was.from, to }
    }),
  })

  /* Open on the selected day; a day picked on the strip scrolls there (recentring when it is far off). */
  const opened = useRef(false)
  useLayoutEffect(() => {
    if (opened.current && date === reported.current) return
    opened.current = true
    reported.current = date
    setRange((was) => (date < was.from || date > was.to
      ? { from: addDays(date, -AROUND.before), to: addDays(date, AROUND.after) } : was))
    setTop(date)
    scrollToKey(date)
  }, [date, scrollToKey])

  const tick = useCallback(async (item: CalendarItem) => {
    /* Refused or not, the list asks again and shows what the server holds. */
    await activityRequest(`/activities/${item.id}/done`, { method: 'POST', body: { done: !item.done } }).catch(() => undefined)
    reload()
  }, [reload])

  return (
    /* One scroller whoever is shown, so the list keeps listening to it when the people change. */
    <div ref={scroller} data-phone-view="list"
      className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain bg-bg [line-height:normal] [overflow-anchor:none]">
      {people.length === 0 && <div className="p-4 text-[13px] text-text-muted">Unassigned has no calendar.</div>}
      {people.length > 0 && days.map((day) => {
        const month = monthKeyOf(day)
        return (
          <AgendaDay key={day} day={day} today={today} now={day === today ? now : 0} answer={answers.get(month) ?? null}
            error={errors[month] ?? null} several={people.length > 1} show={show} onTick={tick} />
        )
      })}
    </div>
  )
}

interface DayProps {
  day: string
  today: string
  now: number
  answer: CalendarAnswer | null
  error: string | null
  several: boolean
  show: { visits: boolean; tasks: boolean; blocks: boolean; drives: boolean }
  onTick: (item: CalendarItem) => Promise<void>
}

/**
 * One day: its sticky heading, then what is on it, or *No events*. Drawn again
 * only when its own month's answer, the clock (today only) or Show changes —
 * not every time another day reaches the top.
 */
const AgendaDay = memo(function AgendaDay({ day, today, now, answer, error, several, show, onTick }: DayProps) {
  const gone = day < today
  const isToday = day === today
  const { lead, words } = agendaHeading(day, today)
  const weather = answer?.dates.find((d) => d.date === day)?.weather ?? null
  const entries = entriesOn(answer, day, show)
  const person = several ? null : answer?.people[0] ?? null
  const own = person?.days.find((d) => d.date === day) ?? null

  let body: ReactNode
  if (!answer) {
    body = <div className="flex h-9 items-center text-[13px] text-text-faint">{error ?? 'Loading…'}</div>
  } else {
    body = entries.length === 0 ? (
      /* An empty day is one row, *Off* beside it when the one person shown does not work that day. */
      <div className="flex min-h-8 items-center text-[13px] text-text-muted" data-empty="">
        No events{!several && own && !own.working && own.offWords ? ` · ${own.offWords}` : ''}
      </div>
    ) : (
      <>
        <SubLine entries={entries} own={own} several={several} drives={show.drives} />
        <DayRows entries={entries} own={own} several={several} drives={show.drives && !several}
          gone={gone} isToday={isToday} now={now} onTick={onTick} />
      </>
    )
  }

  return (
    <section data-day={day}
      style={gone ? { background: PAST_TINT } : undefined}>
      <h2 className="sticky top-0 z-[3] m-0 flex h-[34px] items-center gap-2 border-b border-line-faint bg-surface px-3.5 text-[13.5px] font-bold"
        style={gone ? { boxShadow: `inset 0 0 0 999px ${PAST_TINT}` } : undefined}>
        <span className={cn('min-w-0 truncate', gone && 'text-text-muted')}>
          {lead && <span className={isToday ? 'text-accent-dark' : undefined}>{lead} · </span>}{words}
        </span>
        <span className="flex-1" />
        {weather && <Forecast weather={weather} textSize={12} />}
      </h2>
      <div className="px-3.5 pb-2.5 pt-2">{body}</div>
    </section>
  )
})

/** *4 stops · about 52 min driving*, or *Off — Tom works Mon–Thu* (Board 13 A's day line). */
function SubLine({ entries, own, several, drives }: {
  entries: DayEntry[]; own: ActivityDay | null; several: boolean; drives: boolean
}) {
  const stops = entries.filter(({ item }) => isStop(item)).length
  if (!several && own && !own.working && own.offWords) {
    return <div className="pb-1.5 text-[12px] text-text-muted">{own.offWords}</div>
  }
  if (stops === 0) return null
  return (
    <div className="pb-1.5 text-[12px] text-text-muted">
      {stops} {stops === 1 ? 'stop' : 'stops'}
      {/* The driving total goes with Drive times (§6.3). */}
      {!several && drives && own && own.driving > 0 ? ` · ${aboutDrivingWords(own.driving)}` : ''}
    </div>
  )
}

/**
 * A day's rows in time order: the leave line before the first stop, a drive
 * line between stops, the now line where now is on today — and on today
 * everything above it in the past band (§6.1).
 */
function DayRows({ entries, own, several, drives, gone, isToday, now, onTick }: {
  entries: DayEntry[]; own: ActivityDay | null; several: boolean; drives: boolean; gone: boolean; isToday: boolean
  now: number; onTick: (item: CalendarItem) => Promise<void>
}) {
  const allDay: ReactNode[] = []
  const before: ReactNode[] = []
  const after: ReactNode[] = []
  let nowPlaced = !isToday
  let rows = before
  /* The now line opens what is still to come; everything above it today is the past band. */
  const placeNow = () => {
    nowPlaced = true
    rows = after
    rows.push(<NowLine key="now" minutes={now} />)
  }
  /* A whole day off heads the day, outside the band — it is all of today, not its past. */
  for (const { item, person } of entries.filter(({ item }) => item.allDay)) {
    allDay.push(<BlockRow key={item.key} item={item} person={several ? person : undefined} allDay />)
  }
  const timed = entries.filter(({ item }) => !item.allDay)
  /* The leave line, with the stops it leads to (§6.3) — *Left …* once its time has gone. */
  if (own?.leave && drives && timed.some(({ item }) => isStop(item))) {
    const left = gone || (isToday && now >= own.leave.atMinutes)
    if (!nowPlaced && !left) placeNow()
    rows.push(
      <div key="leave" className="mb-1.5 mt-0.5 flex items-center gap-2 pl-14 text-[12px] font-semibold text-text-muted">
        <Glyph name="house" size={13} />{left ? own.leave.leftWords : own.leave.words}
      </div>,
    )
  }
  let firstStop = true
  for (const { item, person } of timed) {
    if (!nowPlaced && now < item.start) placeNow()
    if (isStop(item)) {
      const leg = drives && own ? own.drives.find((d) => d.key === item.key) ?? null : null
      if (leg && !firstStop) rows.push(<DriveLine key={`d-${item.key}`} drive={leg} />)
      firstStop = false
      rows.push(<StopCard key={item.key} item={item} now={now} isToday={isToday} person={several ? person : undefined} />)
    } else if (item.kind === 'task') {
      rows.push(<TaskRow key={item.key} item={item} person={several ? person : undefined} onTick={() => void onTick(item)} />)
    } else {
      rows.push(<BlockRow key={item.key} item={item} person={several ? person : undefined} />)
    }
    /* The now line sits inside a stop that is happening, under its card (Board 13 A). */
    if (!nowPlaced && isToday && item.start <= now && now < item.end) placeNow()
  }
  if (!nowPlaced) placeNow()
  return (
    <>
      {allDay}
      {/* Today above the now line is in the past band (§6.1, Board 13 A). */}
      {isToday && before.length > 0
        ? <div className="-mx-3.5 px-3.5 pb-0.5" style={{ background: PAST_TINT }}>{before}</div>
        : before}
      {after}
    </>
  )
}

/** The drive between two cards: a dotted rule, a car and *8 min · 3.1 mi*. */
function DriveLine({ drive }: { drive: CalendarDrive }) {
  return (
    <div title={drive.tooltip} className={cn('my-1 flex items-center gap-2 pl-14 text-[12px] font-semibold', DRIVE_TONE[drive.tone])}>
      <i className="mx-[5px] ml-1.5 block h-4 w-0 border-l-2 border-dotted border-line-strong" />
      <Glyph name="car" size={13} />
      {drive.minutes} min · {drive.miles.toFixed(1)} mi
    </div>
  )
}

/** The now line: *9:41* in red, a dot and a rule. */
function NowLine({ minutes }: { minutes: number }) {
  return (
    <div className="my-0.5 flex items-center gap-1.5" data-now-line>
      <span className="w-12 text-right text-[11px] font-bold" style={{ color: NOW_RED }}>{clock(minutes)}</span>
      <i className="block h-[7px] w-[7px] rounded-full" style={{ background: NOW_RED }} />
      <div className="h-0.5 flex-1" style={{ background: NOW_RED }} />
    </div>
  )
}

/** A stop's card: the 44 px time column, the edge, the title, the tags, pin + City, ST, the job (Board 13 A). */
function StopCard({
  item, now, isToday, person,
}: { item: CalendarItem; now: number; isToday: boolean; person?: CalendarPersonDays }) {
  const kind = item.kind === 'appointment' ? KIND_STYLES.site_visit : KIND_STYLES.meeting
  /* One person: the kind's colour; several: the person's (§10.2). */
  const edge = person ? toneOf(person.colour).edge : kind.fg
  const current = isToday && item.start <= now && now < item.end
  const city = item.place?.city ? `${item.place.city}${item.place.state ? `, ${item.place.state}` : ''}` : null
  return (
    <div className="flex items-start gap-3">
      <div className="flex w-11 shrink-0 flex-col items-end pt-2.5">
        <b className="text-[13px]">{clock(item.start)}</b>
        <span className="text-[11.5px] text-text-faint">{clock(item.end)}</span>
      </div>
      <button type="button" onClick={() => openCalendarItem(item)} data-stop={item.key}
        className="relative min-w-0 flex-1 overflow-hidden rounded-xl border border-line bg-surface py-2.5 pl-[15px] pr-3 text-left">
        <i className="absolute inset-y-0 left-0 w-1" style={{ background: edge }} aria-hidden />
        <span className="flex items-center gap-1.5">
          <b className="truncate text-[14.5px]">{item.title}</b>
          <span className="flex-1" />
          {item.tags.map((tag) => (
            <span key={tag} className="inline-flex h-4 items-center rounded px-1.5 text-[9.5px] font-semibold bg-accent-soft text-accent-dark">{tag}</span>
          ))}
          {person && <Face person={person} size={22} />}
        </span>
        {city && !item.remote && (
          <span className="mt-[3px] flex items-center gap-1 text-[13px] font-semibold text-text-body">
            <Glyph name="pin" size={13} />{city}
          </span>
        )}
        {item.sub && <span className="mt-0.5 block truncate text-[12px] text-text-muted">{item.sub}</span>}
        {current && (
          <span className="mt-1.5 flex items-center gap-[5px] text-[11.5px] font-semibold text-accent-dark">
            <Glyph name="clock" size={12} />{minutesLeftWords(item.end - now)}
          </span>
        )}
      </button>
    </div>
  )
}

/** A timed task: an amber 44 px row — its circle ticks it, the rest opens the Task sheet (Board 13 A, §8.2). */
function TaskRow({ item, person, onTick }: { item: CalendarItem; person?: CalendarPersonDays; onTick: () => void }) {
  return (
    <div className="my-1.5 flex items-center gap-3">
      <div className="w-11 shrink-0 text-right text-[13px] font-semibold" style={{ color: TASK_TEXT }}>{clock(item.start)}</div>
      <div className="flex h-11 min-w-0 flex-1 items-center gap-1 rounded-[10px] border bg-surface pl-1 pr-3 text-[13.5px] font-semibold"
        style={{ borderColor: TASK_LINE, color: item.done ? undefined : TASK_TEXT }}>
        <button type="button" aria-label={item.done ? 'Mark not done' : 'Mark done'} onClick={onTick}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center">
          <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full text-white"
            style={{ border: `1.8px solid ${TASK_BOX}`, background: item.done ? TASK_BOX : 'rgb(var(--color-surface))' }}>
            {item.done && <Glyph name="check" size={11} stroke={3} />}
          </span>
        </button>
        <button type="button" onClick={() => openCalendarItem(item)} data-task={item.key}
          className={cn('flex h-10 min-w-0 flex-1 items-center gap-2 text-left', item.done && 'text-text-faint')}>
          <span className={cn('min-w-0 flex-1 truncate', item.done && 'line-through')}>{item.title}</span>
          {person && <Face person={person} size={18} />}
          <span className="text-[11.5px] font-medium text-text-muted">Task</span>
        </button>
      </div>
    </div>
  )
}

/** A time block: a hatched row; a whole day off reads *All day · Time off* (§10.2). */
function BlockRow({ item, person, allDay = false }: { item: CalendarItem; person?: CalendarPersonDays; allDay?: boolean }) {
  return (
    <div className="my-1.5 flex items-center gap-3" data-block={item.key}>
      <div className={cn('w-11 shrink-0 text-right font-semibold text-text-muted', allDay ? 'text-[11.5px]' : 'text-[13px]')}>
        {allDay ? 'All day' : clock(item.start)}
      </div>
      <div className="flex h-10 min-w-0 flex-1 items-center gap-2.5 rounded-[10px] border border-line px-3 text-[13.5px] font-semibold text-text-body"
        style={{ background: HATCH }}>
        <span className="min-w-0 flex-1 truncate">{allDay ? 'Time off' : item.title}</span>
        {person && <Face person={person} size={18} />}
        {!allDay && <span className="text-[11.5px] font-medium text-text-muted">{clock(item.end)}</span>}
      </div>
    </div>
  )
}
